import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    gradient: {
      primary: string;
      secondary: string;
      success: string;
      dark: string;
      light: string;
    };
    neutral: {
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
  }
  interface PaletteOptions {
    gradient?: {
      primary?: string;
      secondary?: string;
      success?: string;
      dark?: string;
      light?: string;
    };
    neutral?: {
      100?: string;
      200?: string;
      300?: string;
      400?: string;
      500?: string;
      600?: string;
      700?: string;
      800?: string;
      900?: string;
    };
  }
}

export const createAppTheme = (mode: 'light' | 'dark') => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
      light: mode === 'dark' ? '#93c5fd' : '#34495e',
      dark: mode === 'dark' ? '#65f0c5' : '#1a252f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: mode === 'dark' ? '#34d399' : '#3498db',
      light: mode === 'dark' ? '#6ee7b7' : '#5dade2',
      dark: mode === 'dark' ? '#10b981' : '#2874a6',
      contrastText: '#ffffff',
    },
    error: {
      main: mode === 'dark' ? '#f87171' : '#c62828',
      light: mode === 'dark' ? '#fca5a5' : '#ff5f52',
      dark: mode === 'dark' ? '#dc2626' : '#8e0000',
    },
    warning: {
      main: mode === 'dark' ? '#fbbf24' : '#f06e65',
      light: mode === 'dark' ? '#fcd34d' : '#ff9d3f',
      dark: mode === 'dark' ? '#f59e0b' : '#b53d00',
    },
    info: {
      main: mode === 'dark' ? '#65f0c5' : '#0288d1',
      light: mode === 'dark' ? '#93c5fd' : '#5eb8ff',
      dark: mode === 'dark' ? '#65f0c5' : '#005b9f',
    },
    success: {
      main: mode === 'dark' ? '#4ade80' : '#35e87c',
      light: mode === 'dark' ? '#86efac' : '#60ad5e',
      dark: mode === 'dark' ? '#22c55e' : '#005005',
    },
    gradient: {
      primary: mode === 'dark'
        ? 'linear-gradient(135deg, #65f0c5 0%, #65f0c5 100%)'
        : 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)',
      secondary: mode === 'dark'
        ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
        : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      success: mode === 'dark'
        ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
        : 'linear-gradient(135deg, #13B497 0%, #0F7B6C 100%)',
      dark: mode === 'dark'
        ? 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)'
        : 'linear-gradient(135deg, #1a237e 0%, #000051 100%)',
      light: mode === 'dark'
        ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
        : 'linear-gradient(135deg, #E3F2FD 0%, #FFFFFF 100%)',
    },
    neutral: {
      100: mode === 'dark' ? '#F8FAFC' : '#FAFBFC',
      200: mode === 'dark' ? '#F1F5F9' : '#F4F6F8',
      300: mode === 'dark' ? '#E2E8F0' : '#DFE3E8',
      400: mode === 'dark' ? '#CBD5E1' : '#C4CDD5',
      500: mode === 'dark' ? '#94A3B8' : '#919EAB',
      600: mode === 'dark' ? '#64748B' : '#637381',
      700: mode === 'dark' ? '#475569' : '#454F5B',
      800: mode === 'dark' ? '#334155' : '#2B3442',
      900: mode === 'dark' ? '#1E293B' : '#161C24',
    },
    background: {
      default: mode === 'dark' ? '#313131' : '#FAFBFC',
      paper: mode === 'dark' ? '#1a1a1a' : '#FFFFFF',
    },
    text: {
      primary: mode === 'dark' ? '#F8FAFC' : '#2B3442',
      secondary: mode === 'dark' ? '#CBD5E1' : '#637381',
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.875rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.6,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: '0.009em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.75,
      letterSpacing: '0.009em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    button: {
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.7,
      letterSpacing: '0.05em',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.03em',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: 1.6,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.05)',
    '0px 8px 16px rgba(0,0,0,0.05)',
    '0px 12px 24px rgba(0,0,0,0.05)',
    '0px 16px 32px rgba(0,0,0,0.05)',
    '0px 20px 40px rgba(0,0,0,0.05)',
    '0px 24px 48px rgba(0,0,0,0.06)',
    '0px 28px 56px rgba(0,0,0,0.07)',
    '0px 32px 64px rgba(0,0,0,0.08)',
    '0px 36px 72px rgba(0,0,0,0.09)',
    '0px 40px 80px rgba(0,0,0,0.10)',
    '0px 44px 88px rgba(0,0,0,0.11)',
    '0px 48px 96px rgba(0,0,0,0.12)',
    '0px 52px 104px rgba(0,0,0,0.13)',
    '0px 56px 112px rgba(0,0,0,0.14)',
    '0px 60px 120px rgba(0,0,0,0.15)',
    '0px 64px 128px rgba(0,0,0,0.16)',
    '0px 68px 136px rgba(0,0,0,0.17)',
    '0px 72px 144px rgba(0,0,0,0.18)',
    '0px 76px 152px rgba(0,0,0,0.19)',
    '0px 80px 160px rgba(0,0,0,0.20)',
    '0px 84px 168px rgba(0,0,0,0.21)',
    '0px 88px 176px rgba(0,0,0,0.22)',
    '0px 92px 184px rgba(0,0,0,0.23)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: mode === 'dark' ? '#1a1a1a #313131' : '#C4CDD5 #F4F6F8',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#1a1a1a' : '#C4CDD5',
            border: `2px solid ${mode === 'dark' ? '#313131' : '#F4F6F8'}`,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            borderRadius: 8,
            backgroundColor: mode === 'dark' ? '#313131' : '#F4F6F8',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          letterSpacing: '0.01em',
          transition: 'all 0.2s ease',
          boxShadow: 'none',
          textTransform: 'none',
          '&:hover': {
            boxShadow: 'none',
            transform: 'none',
          },
        },
        contained: {
          backgroundColor: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
          '&:hover': {
            backgroundColor: mode === 'dark' ? '#65f0c5' : '#34495e',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
          color: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
          '&:hover': {
            borderWidth: '1px',
            borderColor: mode === 'dark' ? '#65f0c5' : '#34495e',
            backgroundColor: mode === 'dark' ? 'rgba(96, 165, 250, 0.08)' : 'rgba(44, 62, 80, 0.04)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          backgroundImage: 'none',
          transition: 'all 0.2s ease',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: mode === 'dark' ? '0px 1px 3px rgba(0,0,0,0.3)' : '0px 1px 3px rgba(0,0,0,0.12)',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#e0e0e0'}`,
          background: mode === 'dark' ? '#1a1a1a' : '#ffffff',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: mode === 'dark' ? '0px 4px 12px rgba(0,0,0,0.4)' : '0px 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: mode === 'dark' ? '#313131' : '#FAFBFC',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: mode === 'dark' ? '#313131' : '#E3F2FD',
              borderWidth: '2px',
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
              borderColor: mode === 'dark' ? '#313131' : '#1a1a1a',
            },
            '&.Mui-focused fieldset': {
              borderColor: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: mode === 'dark' ? '#CBD5E1' : '#637381',
            '&.Mui-focused': {
              color: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          letterSpacing: '0.025em',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
        filled: {
          background: mode === 'dark'
            ? 'linear-gradient(135deg, #65f0c5 0%, #65f0c5 100%)'
            : 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)',
          color: '#FFFFFF',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.12)',
          borderRadius: 0,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 56,
        },
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          background: mode === 'dark'
            ? 'linear-gradient(90deg, #00f5ff 0%, #00f5ff 100%)'
            : 'linear-gradient(90deg, #1a1a1a 0%, #1a1a1a 100%)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
          fontWeight: 600,
          letterSpacing: '0.025em',
          minHeight: 56,
          transition: 'all 0.3s ease',
          '&:hover': {
            color: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
            opacity: 1,
          },
          '&.Mui-selected': {
            color: mode === 'dark' ? '#65f0c5' : '#1a1a1a',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(102, 126, 234, 0.08)',
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: mode === 'dark' ? '#313131' : 'transparent',
          '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
            borderColor: mode === 'dark' ? '#313131' : '#E3F2FD',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === 'dark' ? '#313131' : '#1a1a1a',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1a1a1a',
            borderWidth: '2px',
          },
        },
      },
    },
  },
});

const theme = createAppTheme('light');
export default theme;